import * as React from 'react';
import { Formik, FormikErrors, FormikProps, FormikValues, Form } from 'formik';
import { useIntl } from 'react-intl';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { CmdAlert } from '@commander-services/gui-components';
import localforage from 'localforage';
import LoginInput from '../Forms/Fields/LoginInput';
import { getDefaultCustomer, getLanguage } from '../../Services/UserService';
import LoginService from '../../Services/LoginService';
import JWTService from '../../Services/JWTService';
import { LanguageContextProps, defaultLanguage } from '../LanguagesContext';
import VerifyService from '../../Services/VerifyService';
import * as LoginState from '../Login/LoginState';
import usePageWithForm from '../../hooks/usePageWithForm';
import { gdprSettingsAtom } from '../../store/recoil/AppState';
import { selectedCustomersAtom } from '../../store/recoil/customers';
import { selectedVehiclesAtom } from '../../store/recoil/vehicles';
import { activeLanguageAtom } from '../MainMenu/MainMenuState';
import useAnalytics from '../../hooks/useAnalytics';

const RESPONSE_OK = 'ok';
const RESPONSE_WARNING = 'warning';

interface IVerifydata {
  id: number;
  phone: string;
}

interface IVerifyProps {
  data: IVerifydata | null;
  context: LanguageContextProps;
  stateProps: { username: string | null; password: string | null; recaptchaToken: string | null };
  resend: () => void;
}

interface IAlert {
  title: string;
  message: string;
  type: string;
}

function Verify(props: IVerifyProps): JSX.Element {
  usePageWithForm();
  const { trackEvent } = useAnalytics();
  const { formatMessage: f } = useIntl();
  const { setUsername: setUsernameToAnalytics } = useAnalytics();
  const { data, context: propsVerify, resend } = props;
  const [isButtonLoaderActive, setIsButtonLoaderActive] = React.useState<boolean>(false);
  const [alert, setAlert] = React.useState<IAlert | null>(null);
  const [verifyData, setVerifyData] = React.useState<IVerifydata | null>(null);
  const setToken = useRecoilState<string | null>(LoginState.token)[1];
  const setGdprSettings = useSetRecoilState<Record<string, any>>(gdprSettingsAtom);
  const setLanguage = useSetRecoilState<string | null>(activeLanguageAtom);
  const setSelectedCustomers = useSetRecoilState<number[]>(selectedCustomersAtom);
  const setSelectedVehicles = useSetRecoilState<number[]>(selectedVehiclesAtom);

  const [counter, setCounter] = React.useState<number>(0);

  React.useEffect(() => {
    const time = setInterval(() => {
      if (counter > 0) {
        setCounter(counter - 1);
      }
    }, 1000);
    return () => {
      clearInterval(time);
    };
  }, [counter]);

  React.useEffect(() => {
    setVerifyData(data);
  }, [data]);

  // validation
  const handleValidate = (values: FormikValues) => {
    const errors: FormikErrors<FormikValues> = {};

    if (!values.verification) {
      errors.verification = f({ id: 'waypoints.edit.title.required' });
    }

    return errors;
  };

  // save
  const handleSubmit = async (values: FormikValues) => {
    setIsButtonLoaderActive(true);
    trackEvent(['login (verify)', 'click', 'Submit form']);

    if (verifyData) {
      const verifyCode = {
        code: values.verification,
      };
      const response = await VerifyService.verify(verifyData.id, verifyCode);

      if (response && response.data && response.data.status === RESPONSE_OK) {
        trackEvent(['login (verify)', 'submit', 'success']);
        setAlert(null);
        const gdprSettings = await localforage.setItem('gdprSettings', response.data.gdprProfiles);
        LoginService.setPermissions(response.data.customers);
        setGdprSettings(response.data.gdprProfiles);
        LoginService.setUserData(response.data.user);
        setUsernameToAnalytics(response.data.user.username);
        JWTService.storeSocketServerToken(response.data.socketServerToken);
        JWTService.storeToken(response.data.token);
        setToken(response.data.token);
        setSelectedCustomers(getDefaultCustomer());
        setLanguage(getLanguage());
        setSelectedVehicles([]);

        if (propsVerify.updateValue) {
          const language = getLanguage();
          if (language) {
            propsVerify.updateValue('language', language);
          }
        }

        // if (gdprSettings) {
        //   window.location.reload();
        // }
      } else if (response && response.status === 429) {
        if (response && response.status === 429) {
          trackEvent(['login (verify)', 'submit', 'too many attempts']);
          setAlert({
            title: f({ id: 'toastr.error' }),
            message: f({ id: 'toast.auth.tooManyAttempts' }),
            type: 'error',
          });
          setIsButtonLoaderActive(false);
        }
      } else if (response && response.data.status === RESPONSE_WARNING) {
        trackEvent(['login (verify)', 'submit', response.data.toast.message]);
        setAlert({
          title: f({ id: 'toastr.warning' }),
          message: f({ id: response.data.toast.message }),
          type: 'warning',
        });
      }
    }

    setIsButtonLoaderActive(false);
  };

  // initial values
  const initialValues = {
    verification: '',
  };

  // form
  return (
    <div className="w-login">
      <div className="w-login-content">
        {alert && <CmdAlert id="verify-alert" data={alert} />}
        <div className="b-login">
          <div className="headline">
            <h3 className="title">{f({ id: 'verify.title' })}</h3>
          </div>
          <Formik initialValues={initialValues} validate={handleValidate} onSubmit={handleSubmit}>
            {(formikProps: FormikProps<FormikValues>) => {
              const handleResend = async () => {
                const { username, password, recaptchaToken } = props.stateProps;
                setCounter(60);
                formikProps.setFieldValue('verification', '');

                const response = await LoginService.signIn(
                  username || '',
                  password || '',
                  defaultLanguage,
                  recaptchaToken
                );
                if (response && response.data.status === '2fa_required') {
                  setVerifyData({ id: response.data.id, phone: response.data.phone });
                  setAlert({
                    title: f({ id: 'verify.success.toastr.title' }),
                    message: f({ id: 'verify.success.toastr.message' }),
                    type: 'success',
                  });
                } else if (response && response.status === RESPONSE_WARNING) {
                  setAlert({
                    title: f({ id: 'toastr.warning' }),
                    message: f({ id: response.data.toast.message }),
                    type: response.data.toast,
                  });
                }
              };

              return (
                <Form className="form-horizontal">
                  <div className="p-6">
                    <div className="text-gray fs-1 mb-4">
                      {data && data.phone && (
                        <>
                          {' '}
                          {f(
                            { id: 'verify.sendText' },
                            {
                              phone: `${data.phone
                                .substring(0, data.phone.length - 3)
                                .replace(/./g, '#')}${data.phone.substring(
                                data.phone.length - 3,
                                data.phone.length
                              )}`,
                            }
                          )}
                        </>
                      )}
                    </div>
                    <div className=" mb-1 text-input-component">
                      <LoginInput
                        name="verification"
                        label={f({ id: 'verify.label' })}
                        id="verification"
                        type="text"
                        // placeholder={f({ id: 'verify.placeholder' })}
                        required={true}
                      />
                      {/* <div className="text-center"> */}

                      {/* </div> */}
                    </div>
                  </div>
                  <div className="foot pt-3 px-5 pb-5">
                    <button type="submit" className="e-button e-button--gray d-flex w-100">
                      {f({ id: 'verify.submitButton' })}
                    </button>
                    {counter === 0 && (
                      <a
                        className="fs-2 mt-2 d-inline-block font-weight-bold"
                        onClick={handleResend}
                        style={{ cursor: 'pointer' }}
                      >
                        {`${f({ id: 'verify.resend' })}`}
                      </a>
                    )}
                    {counter > 0 && (
                      <a
                        className="fs-2 mt-2 d-inline-block text-gray-light font-weight-bold"
                        onClick={undefined}
                        style={{ cursor: 'not-allowed' }}
                      >
                        {`${f({ id: 'verify.resend' })} ${counter > 0 ? `(${counter})` : ''}`}
                      </a>
                    )}
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </div>
  );
}

export default Verify;
